import React, { useEffect, useMemo, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { useFetchClientProjectsQuery } from "../../store/api/projectsApi";
import { useClientAuth } from "../../hooks/useClientAuth";
import {
  getProjectProgressSummary,
  getTaskDelayDays,
  getTaskHealth,
} from "../erp/adminData";
import "./client.css";

const ClientDashboardPage = () => {
  const { clientUser } = useClientAuth();
  const {
    data: projects = [],
    isLoading,
    error,
  } = useFetchClientProjectsQuery();
  const [selectedProjectId, setSelectedProjectId] = useState("");

  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const selectedProject = useMemo(
    () =>
      projects.find((project) => project.id === selectedProjectId) ??
      projects[0] ??
      null,
    [projects, selectedProjectId],
  );

  const selectedProjectTasks = selectedProject?.progressTasks ?? [];
  const summary = selectedProject
    ? getProjectProgressSummary({
        ...selectedProject,
        progressTasks: selectedProjectTasks,
      })
    : null;
  const errorMessage = error
    ? getApiErrorMessage(error as FetchBaseQueryError)
    : "";

  return (
    <section>
      <div className='client-hero-card p-4 p-lg-5 mb-4'>
        <div className='row align-items-center g-4'>
          <div className='col-lg-8'>
            <p className='client-login-kicker mb-2'>Project Progress Tracker</p>
            <h1 className='h2 mb-2'>Welcome, {clientUser?.name || "Client"}</h1>
            <p className='text-muted mb-0'>
              This panel is connected to the project API so you can track
              current work packages, timeline movement, and execution notes for
              your own projects directly.
            </p>
          </div>
          <div className='col-lg-4'>
            <div className='client-stat-grid'>
              <article className='client-stat-card'>
                <p>Projects</p>
                <h3>{isLoading ? "..." : projects.length}</h3>
              </article>
              <article className='client-stat-card'>
                <p>Tasks</p>
                <h3>{summary?.totalTasks ?? 0}</h3>
              </article>
              <article className='client-stat-card'>
                <p>Delayed</p>
                <h3>{summary?.delayedTasks ?? 0}</h3>
              </article>
              <article className='client-stat-card'>
                <p>Actual Progress</p>
                <h3>{summary?.averageActual ?? 0}%</h3>
              </article>
            </div>
          </div>
        </div>
      </div>

      {errorMessage ? (
        <div className='alert alert-danger mb-4'>{errorMessage}</div>
      ) : null}
      {isLoading ? (
        <div className='alert alert-light border'>Loading your projects...</div>
      ) : null}

      {!isLoading && projects.length > 0 ? (
        <div className='row g-4'>
          <div className='col-lg-4'>
            <div className='client-project-list'>
              {projects.map((project) => {
                const projectSummary = getProjectProgressSummary(project);
                const isActive = selectedProject?.id === project.id;

                return (
                  <button
                    type='button'
                    key={project.id}
                    className={`client-project-card text-start${isActive ? " active" : ""}`}
                    onClick={() => setSelectedProjectId(project.id)}
                  >
                    <div className='d-flex justify-content-between align-items-start gap-3 mb-2'>
                      <div>
                        <strong className='d-block'>{project.title}</strong>
                        <small className='text-muted'>
                          {project.location || "Location not available"}
                        </small>
                      </div>
                      <span
                        className={`badge rounded-pill ${project.status === "completed" ? "text-bg-success" : project.status === "in-progress" ? "text-bg-primary" : "text-bg-warning"}`}
                      >
                        {project.status}
                      </span>
                    </div>
                    <div className='small text-muted mb-2'>
                      {project.area || "Area pending"} |{" "}
                      {project.rateOfConstruction || "Rate pending"}
                    </div>
                    <div className='d-flex flex-wrap gap-2'>
                      <span className='badge text-bg-dark'>
                        {projectSummary.totalTasks} tasks
                      </span>
                      <span className='badge text-bg-danger'>
                        {projectSummary.delayedTasks} delayed
                      </span>
                      <span className='badge text-bg-success'>
                        {projectSummary.averageActual}% actual
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className='col-lg-8'>
            {selectedProject ? (
              <div className='client-detail-card'>
                <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4'>
                  <div>
                    <h2 className='h4 mb-1'>{selectedProject.title}</h2>
                    <p className='text-muted mb-0'>
                      {selectedProject.description ||
                        "Project description will appear here."}
                    </p>
                  </div>
                  <div className='d-flex flex-wrap gap-2'>
                    <span className='badge text-bg-primary'>
                      Planned {summary?.averagePlanned ?? 0}%
                    </span>
                    <span className='badge text-bg-success'>
                      Actual {summary?.averageActual ?? 0}%
                    </span>
                    <span className='badge text-bg-danger'>
                      {summary?.delayedTasks ?? 0} delayed
                    </span>
                  </div>
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-sm-6'>
                    <strong className='d-block'>Project Owner</strong>
                    <span className='text-muted'>
                      {selectedProject.owner || "-"}
                    </span>
                  </div>
                  <div className='col-sm-6'>
                    <strong className='d-block'>Location</strong>
                    <span className='text-muted'>
                      {selectedProject.location || "-"}
                    </span>
                  </div>
                  <div className='col-sm-6'>
                    <strong className='d-block'>Area</strong>
                    <span className='text-muted'>
                      {selectedProject.area || "-"}
                    </span>
                  </div>
                  <div className='col-sm-6'>
                    <strong className='d-block'>Rate of Construction</strong>
                    <span className='text-muted'>
                      {selectedProject.rateOfConstruction || "-"}
                    </span>
                  </div>
                  <div className='col-sm-6'>
                    <strong className='d-block'>Created</strong>
                    <span className='text-muted'>
                      {selectedProject.createdAt || "-"}
                    </span>
                  </div>
                  <div className='col-sm-6'>
                    <strong className='d-block'>Contract Files</strong>
                    <span className='text-muted'>
                      {Array.isArray(selectedProject.contractFiles) &&
                      selectedProject.contractFiles.length > 0
                        ? selectedProject.contractFiles.join(", ")
                        : "No contract files listed"}
                    </span>
                  </div>
                </div>

                <div className='table-responsive'>
                  <table className='table align-middle'>
                    <thead className='table-light'>
                      <tr>
                        <th>Module</th>
                        <th>Owner</th>
                        <th>Planned</th>
                        <th>Actual</th>
                        <th>Planned End</th>
                        <th>Health</th>
                        <th>Delay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProjectTasks.map((task) => {
                        const health = getTaskHealth(task);
                        return (
                          <tr key={task.id}>
                            <td>
                              <strong>{task.moduleName}</strong>
                              <div className='small text-muted client-task-note'>
                                {task.notes}
                              </div>
                            </td>
                            <td>{task.owner}</td>
                            <td>{task.plannedPercent}%</td>
                            <td>{task.actualPercent}%</td>
                            <td>{task.plannedEndDate || "-"}</td>
                            <td>
                              <span
                                className={`badge rounded-pill ${health === "completed" ? "text-bg-success" : health === "delayed" ? "text-bg-danger" : "text-bg-primary"}`}
                              >
                                {health}
                              </span>
                            </td>
                            <td>
                              {getTaskDelayDays(task) > 0
                                ? `${getTaskDelayDays(task)} day(s)`
                                : "On time"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!selectedProjectTasks.length ? (
                  <div className='alert alert-light border mb-0'>
                    No progress updates have been posted for this project yet.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {!isLoading && projects.length === 0 && !errorMessage ? (
        <div className='client-empty-card p-5 text-center'>
          <h2 className='h4 mb-2'>No projects found for this account</h2>
          <p className='text-muted mb-0'>
            Once your project is assigned to this client login, it will appear
            here automatically with the related progress details.
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default ClientDashboardPage;
